import React, { useEffect, useMemo, useState } from 'react';
import {
  Check,
  ChevronLeft,
  CircleAlert,
  Info,
  Loader2,
  LockKeyhole,
  Mail,
  MailCheck,
  ShieldCheck,
  UserCheck
} from 'lucide-react';
import api, { translateApiError } from '../api/client';
import ditaLogo from '../icons.png';

const THERAPEUTIC_AREAS = [
  { value: 'cardiology', label: 'Cardiologie' },
  { value: 'traumatology_orthopedics', label: 'Traumatologie / Ortopedie' },
  { value: 'neurology', label: 'Neurologie' },
  { value: 'endocrinology_diabetology', label: 'Endocrinologie / Diabetologie' },
  { value: 'family_medicine', label: 'Medicină de familie' },
  { value: 'gynecology_obstetrics', label: 'Ginecologie / Obstetrică' },
  { value: 'allergology_immunology', label: 'Alergologie / Imunologie' },
  { value: 'gastroenterology', label: 'Gastroenterologie' },
  { value: 'pneumology', label: 'Pneumologie' },
  { value: 'orl', label: 'ORL' },
  { value: 'ophthalmology', label: 'Oftalmologie' },
  { value: 'dermatology', label: 'Dermatologie' },
  { value: 'urology', label: 'Urologie' },
  { value: 'nephrology', label: 'Nefrologie' },
  { value: 'rheumatology', label: 'Reumatologie' },
  { value: 'pediatrics', label: 'Pediatrie' },
  { value: 'psychiatry', label: 'Psihiatrie' },
  { value: 'oncology', label: 'Oncologie' },
  { value: 'hematology', label: 'Hematologie' },
  { value: 'infectious_diseases', label: 'Boli infecțioase' },
  { value: 'surgery', label: 'Chirurgie' },
  { value: 'internal_medicine', label: 'Medicină internă' },
  { value: 'dentistry', label: 'Stomatologie' },
  { value: 'nutrition_supplements', label: 'Nutriție / Suplimente alimentare' },
  { value: 'other', label: 'Alte arii' },
];

const ALL_STANDARD_AREAS = THERAPEUTIC_AREAS.filter((a) => a.value !== 'other').map((a) => a.value);

const COMMUNICATION_CHANNELS = [
  { value: 'email', label: 'E-mail', description: 'Buletine informative, oferte comerciale și materiale educaționale pe email' },
  { value: 'phone', label: 'Telefon (apel vocal)', description: 'Contactare telefonică directă de către reprezentantul medical' },
  { value: 'sms', label: 'SMS', description: 'Alerte rapide, invitații la conferințe și evenimente prin mesaje SMS' },
];

const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export default function MarketingConsentUnsubscribePage() {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(null);

  // Stări pentru extragerea datelor din CRM (animatie si date încărcate)
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [hasLookedUp, setHasLookedUp] = useState(false);
  const [existingFound, setExistingFound] = useState(false);
  const [recipientInfo, setRecipientInfo] = useState(null);
  const [lookupError, setLookupError] = useState(null);

  // Opțiuni selectate (se populează după extragere)
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [otherAreaText, setOtherAreaText] = useState('');
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);

  // Stări pentru confirmare prin cod OTP din email
  const [verificationSession, setVerificationSession] = useState(null);
  const [enteredCode, setEnteredCode] = useState('');
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [verificationError, setVerificationError] = useState(null);

  // Stare finală cu succes
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [confirmedDetails, setConfirmedDetails] = useState(null);

  const [resendCooldown, setResendCooldown] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const [resendNotice, setResendNotice] = useState(null);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Dezabonare comunicări de marketing direct | DITA ESTFARM';

    // Asigurare scroll pe pagină
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';

    return () => {
      document.title = previousTitle;
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, []);

  // Timer cooldown 60s pentru retrimitere cod
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Verificare token direct din URL (?token=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenParam = params.get('token');
    if (tokenParam) {
      api.get(`/api/marketing-consent/unsubscribe/session/${encodeURIComponent(tokenParam)}`)
        .then((res) => {
          if (res.data?.token) {
            setVerificationSession({
              token: res.data.token,
              email: res.data.email,
              isPartial: res.data.isPartial,
              requestedAreas: res.data.requestedAreas || [],
              requestedChannels: res.data.requestedChannels || [],
            });
            if (res.data.isConfirmed) {
              setIsConfirmed(true);
              setConfirmedDetails({
                email: res.data.email,
                confirmedAt: res.data.confirmedAt,
                isPartial: res.data.isPartial,
                requestedAreas: res.data.requestedAreas || [],
                requestedChannels: res.data.requestedChannels || [],
              });
            }
          }
        })
        .catch((err) => {
          console.error('Eroare verificare sesiune dezabonare:', err);
        });
    }
  }, []);

  const scrollToTop = () => {
    const mainEl = document.getElementById('unsubscribe-main-container');
    if (mainEl) {
      mainEl.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Căutare automată a acordului activ când utilizatorul introduce o adresă validă
  const handleLookupPreferences = async (lookupEmail) => {
    const clean = String(lookupEmail || '').trim().toLowerCase();
    if (!EMAIL_PATTERN.test(clean)) return;

    setIsLookingUp(true);
    setLookupError(null);
    try {
      const res = await api.get(`/api/marketing-consent/unsubscribe/lookup?email=${encodeURIComponent(clean)}`);
      setHasLookedUp(true);
      if (res.data?.found) {
        setRecipientInfo({
          name: `${res.data.firstName || ''} ${res.data.lastName || ''}`.trim(),
          specialization: res.data.specialization,
          medicalUnit: res.data.medicalUnit,
        });
        setSelectedAreas(Array.isArray(res.data.therapeuticAreas) ? res.data.therapeuticAreas : []);
        setOtherAreaText(res.data.otherTherapeuticArea || '');
        setSelectedChannels(Array.isArray(res.data.communicationChannels) ? res.data.communicationChannels : []);
        setExistingFound(true);
      } else {
        setRecipientInfo(null);
        setSelectedAreas([]);
        setSelectedChannels([]);
        setExistingFound(false);
      }
    } catch (err) {
      console.error('Eroare la extragerea datelor din CRM:', err);
      setLookupError('Nu s-au putut prelua opțiunile existente. Puteți solicita dezabonarea.');
      setHasLookedUp(true);
      setExistingFound(false);
    } finally {
      setIsLookingUp(false);
    }
  };

  // Debounce pentru căutare automată la introducerea emailului
  useEffect(() => {
    const clean = email.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(clean)) {
      setHasLookedUp(false);
      setRecipientInfo(null);
      setSelectedAreas([]);
      setSelectedChannels([]);
      setExistingFound(false);
      return;
    }

    const timer = setTimeout(() => {
      handleLookupPreferences(clean);
    }, 450);

    return () => clearTimeout(timer);
  }, [email]);

  const isAllAreasSelected = useMemo(() => {
    return ALL_STANDARD_AREAS.every((area) => selectedAreas.includes(area));
  }, [selectedAreas]);

  const toggleSelectAllAreas = () => {
    if (isAllAreasSelected) {
      setSelectedAreas(selectedAreas.includes('other') ? ['other'] : []);
    } else {
      setSelectedAreas(selectedAreas.includes('other') ? [...ALL_STANDARD_AREAS, 'other'] : [...ALL_STANDARD_AREAS]);
    }
  };

  const toggleArea = (areaValue) => {
    setSelectedAreas((prev) => {
      const exists = prev.includes(areaValue);
      if (exists) {
        if (areaValue === 'other') setOtherAreaText('');
        return prev.filter((a) => a !== areaValue);
      } else {
        return [...prev, areaValue];
      }
    });
  };

  const toggleChannel = (channelValue) => {
    setSelectedChannels((prev) => {
      const exists = prev.includes(channelValue);
      return exists ? prev.filter((c) => c !== channelValue) : [...prev, channelValue];
    });
  };

  // Determinare dacă acțiunea este Dezabonare sau Actualizare Opțiuni (dacă mai există opțiuni active lăsate)
  const hasActiveOptions = (selectedAreas && selectedAreas.length > 0) || (selectedChannels && selectedChannels.length > 0);
  const isPartialUpdate = existingFound && hasActiveOptions;
  const isEmailValid = EMAIL_PATTERN.test(email.trim().toLowerCase());
  const isButtonDisabled = !isEmailValid || isLookingUp || isSubmittingEmail || !hasLookedUp;

  // Pasul 1: Trimitere solicitare (expediere cod pe email)
  const handleRequestCode = async (e) => {
    e?.preventDefault();
    const cleanEmail = email.trim().toLowerCase();

    if (!cleanEmail) {
      setEmailError('Introduceți adresa de email.');
      return;
    }

    if (!EMAIL_PATTERN.test(cleanEmail)) {
      setEmailError('Introduceți o adresă de email validă (ex: nume@domeniu.md).');
      return;
    }

    setIsSubmittingEmail(true);
    setEmailError(null);
    setResendNotice(null);

    try {
      const res = await api.post('/api/marketing-consent/unsubscribe/request', {
        email: cleanEmail,
        requestedAreas: selectedAreas,
        otherRequestedArea: otherAreaText,
        requestedChannels: selectedChannels,
      });

      setVerificationSession({
        token: res.data.token,
        email: res.data.email || cleanEmail,
        isPartial: res.data.isPartial,
        requestedAreas: selectedAreas,
        requestedChannels: selectedChannels,
      });
      setEnteredCode('');
      setVerificationError(null);
      setResendCooldown(60);
      scrollToTop();
    } catch (err) {
      console.error('Eroare solicitare cod dezabonare:', err);
      setEmailError(translateApiError(err));
    } finally {
      setIsSubmittingEmail(false);
    }
  };

  // Retrimitere cod OTP
  const handleResendCode = async () => {
    if (resendCooldown > 0 || isResending || !verificationSession?.email) return;

    setIsResending(true);
    setVerificationError(null);
    setResendNotice(null);

    try {
      const res = await api.post('/api/marketing-consent/unsubscribe/request', {
        email: verificationSession.email,
        requestedAreas: verificationSession.requestedAreas || selectedAreas,
        otherRequestedArea: otherAreaText,
        requestedChannels: verificationSession.requestedChannels || selectedChannels,
      });

      setVerificationSession((prev) => ({
        ...prev,
        token: res.data.token || prev.token,
      }));
      setResendCooldown(60);
      setResendNotice('Un nou cod de securitate a fost transmis pe emailul dumneavoastră.');
    } catch (err) {
      setVerificationError(translateApiError(err));
    } finally {
      setIsResending(false);
    }
  };

  // Pasul 2: Verificare cod de 6 cifre introdus de utilizator
  const handleVerifyCode = async (e) => {
    e?.preventDefault();
    const cleanCode = enteredCode.trim().replace(/\D/g, '');

    if (!verificationSession?.token || cleanCode.length !== 6) {
      setVerificationError('Introduceți codul complet din 6 cifre primit pe email.');
      return;
    }

    setIsVerifyingCode(true);
    setVerificationError(null);
    setResendNotice(null);

    try {
      const res = await api.post('/api/marketing-consent/unsubscribe/verify', {
        token: verificationSession.token,
        code: cleanCode,
        email: verificationSession.email,
      });

      setIsConfirmed(true);
      setConfirmedDetails({
        email: res.data.email || verificationSession.email,
        confirmedAt: res.data.confirmedAt || new Date().toISOString(),
        isPartial: res.data.isPartial,
        requestedAreas: res.data.requestedAreas || verificationSession.requestedAreas || [],
        requestedChannels: res.data.requestedChannels || verificationSession.requestedChannels || [],
        affectedConsents: res.data.affectedConsents,
      });
      scrollToTop();
    } catch (err) {
      console.error('Eroare validare cod dezabonare:', err);
      setVerificationError(translateApiError(err));
    } finally {
      setIsVerifyingCode(false);
    }
  };

  const handleResetToEmail = () => {
    setVerificationSession(null);
    setEnteredCode('');
    setVerificationError(null);
    setEmailError(null);
    setIsConfirmed(false);
    setConfirmedDetails(null);
    window.history.replaceState({}, document.title, window.location.pathname);
    scrollToTop();
  };

  const formatSelectedAreasList = (areas) => {
    if (!Array.isArray(areas) || areas.length === 0) return 'Niciuna';
    return areas.map((a) => {
      if (a === 'other' && otherAreaText) return `Alte: ${otherAreaText}`;
      const found = THERAPEUTIC_AREAS.find((th) => th.value === a);
      return found ? found.label : a;
    }).join(', ');
  };

  const formatSelectedChannelsList = (channels) => {
    if (!Array.isArray(channels) || channels.length === 0) return 'Niciunul';
    return channels.map((c) => {
      const found = COMMUNICATION_CHANNELS.find((ch) => ch.value === c);
      return found ? found.label : c;
    }).join(', ');
  };

  // =========================================================================
  // ECRANUL 3: Confirmare cu Succes (Dezabonare totală sau actualizare opțiuni)
  // =========================================================================
  if (isConfirmed) {
    const details = confirmedDetails || verificationSession || {};
    const wasPartial = Boolean(details.isPartial);

    return (
      <main
        id="unsubscribe-main-container"
        className="fixed inset-0 z-50 overflow-y-auto bg-gradient-to-br from-slate-50 via-slate-100 to-slate-200 px-4 py-8 sm:py-14 text-slate-900"
      >
        <section
          className={`mx-auto max-w-[660px] rounded-3xl bg-white p-6 sm:p-10 text-center shadow-xl border border-slate-200 border-t-8 ${
            wasPartial ? 'border-t-blue-600' : 'border-t-emerald-500'
          }`}
          aria-labelledby="confirmed-title"
        >
          {/* Cerc icon */}
          <div
            className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full text-white shadow-lg ${
              wasPartial
                ? 'bg-gradient-to-tr from-blue-700 to-blue-500 shadow-blue-500/25'
                : 'bg-gradient-to-tr from-emerald-600 to-emerald-400 shadow-emerald-500/25'
            }`}
          >
            <Check size={36} className="stroke-[3]" aria-hidden="true" />
          </div>

          <div className="mt-5 inline-block rounded-full bg-slate-100 px-3.5 py-1 text-[11.5px] font-bold tracking-wider uppercase text-slate-700 border border-slate-200">
            S.R.L. DITA ESTFARM
          </div>

          <h1 id="confirmed-title" className="mt-3.5 text-2xl sm:text-[26px] font-extrabold text-slate-950 tracking-tight">
            {wasPartial ? 'Preferințe Actualizate cu Succes!' : 'Dezabonare Confirmată cu Succes!'}
          </h1>

          <p className="mx-auto mt-2.5 max-w-md text-[13.5px] leading-relaxed text-slate-600">
            {wasPartial
              ? 'Opțiunile dumneavoastră privind comunicările de marketing direct au fost actualizate pentru adresa:'
              : 'Consimțământul pentru comunicările de marketing direct a fost retras integral pentru adresa:'}
          </p>
          <p className="mt-1 font-mono text-base font-bold text-blue-900 break-all bg-blue-50/70 inline-block px-4 py-1.5 rounded-lg border border-blue-200">
            {details.email}
          </p>

          {/* Caseta Detalii */}
          <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50/70 p-4 sm:p-6 text-left text-[13px] leading-5 text-slate-800">
            <div className="divide-y divide-dashed divide-slate-200">
              <div className="flex flex-col sm:flex-row sm:justify-between py-2 gap-1">
                <span className="text-slate-500 font-medium">Adresă de email:</span>
                <span className="font-bold text-slate-900 sm:text-right">{details.email}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:justify-between py-2 gap-1">
                <span className="text-slate-500 font-medium">Stare marketing direct:</span>
                <span className={`font-bold sm:text-right ${wasPartial ? 'text-blue-700' : 'text-emerald-700'}`}>
                  {wasPartial ? '✓ Preferințe actualizate (Dezabonare parțială)' : '✓ Retras integral (Dezabonat)'}
                </span>
              </div>
              {wasPartial && (
                <>
                  <div className="flex flex-col sm:flex-row sm:justify-between py-2 gap-1">
                    <span className="text-slate-500 font-medium">Canale de comunicare aprobate:</span>
                    <span className="font-bold text-blue-800 sm:text-right">
                      {formatSelectedChannelsList(details.requestedChannels)}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between py-2 gap-1">
                    <span className="text-slate-500 font-medium shrink-0">Arii terapeutice selectate:</span>
                    <span className="font-bold text-slate-900 sm:text-right max-w-sm">
                      {formatSelectedAreasList(details.requestedAreas)}
                    </span>
                  </div>
                </>
              )}
              {details.confirmedAt && (
                <div className="flex flex-col sm:flex-row sm:justify-between py-2 gap-1">
                  <span className="text-slate-500 font-medium">Data și ora confirmării:</span>
                  <span className="font-semibold text-slate-900 sm:text-right">
                    {new Date(details.confirmedAt).toLocaleString('ro-RO')}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Notă legală */}
          <div className="mt-6 rounded-2xl border border-slate-200/90 bg-slate-50/80 p-4 sm:p-5 text-left text-[12px] leading-relaxed text-slate-600 space-y-2">
            <div className="flex items-start gap-2.5">
              <ShieldCheck size={18} className="text-emerald-600 mt-0.5 shrink-0" />
              <p className="flex-1">
                Solicitarea dumneavoastră a fost înregistrată conform{' '}
                <strong>Legii Republicii Moldova nr. 195 din 25.07.2024 privind protecția datelor cu caracter personal</strong>.
                {wasPartial
                  ? ' Veți primi comunicări promoționale exclusiv pentru ariile și canalele selectate.'
                  : ' Nu veți mai primi buletine informative, oferte comerciale sau comunicări promoționale.'}
              </p>
            </div>
            <div className="flex items-start gap-2.5 pt-2 border-t border-slate-200/70">
              <Info size={16} className="text-blue-600 mt-0.5 shrink-0" />
              <p className="flex-1">
                Modificarea opțiunilor sau retragerea consimțământului de marketing direct nu afectează relația profesională/comercială existentă cu S.R.L. DITA ESTFARM și prelucrarea datelor în CRM în scopul organizării vizitelor în temeiul interesului legitim.
              </p>
            </div>
          </div>

          <p className="mt-6 text-center text-[12.5px] font-medium text-slate-500">
            Vă mulțumim! Puteți închide această pagină în siguranță.
          </p>

          <div className="mt-5 text-center">
            <a
              href="/marketing-consent"
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-slate-100 px-5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-200 transition-colors"
            >
              Mergeți la formularul complet de consimțământ
            </a>
          </div>
        </section>
      </main>
    );
  }

  // =========================================================================
  // ECRANUL 2: Introducere Cod din 6 Cifre (OTP primit pe email)
  // =========================================================================
  if (verificationSession) {
    const isPartial = Boolean(verificationSession.isPartial);

    return (
      <main
        id="unsubscribe-main-container"
        className="fixed inset-0 z-50 overflow-y-auto bg-slate-50 px-4 py-10 text-slate-900 sm:py-16"
      >
        <section
          className="mx-auto max-w-lg rounded-2xl bg-white px-6 py-10 shadow-sm border border-slate-200 sm:px-10 sm:py-12"
          aria-labelledby="otp-title"
        >
          <div className="text-center mb-6">
            <img src={ditaLogo} alt="DITA ESTFARM" className="h-auto w-32 mx-auto" />
          </div>

          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-blue-50 text-blue-700 shadow-sm">
            <LockKeyhole size={28} aria-hidden="true" />
          </div>

          <h1 id="otp-title" className="mt-5 text-center text-2xl font-bold text-slate-950 sm:text-3xl">
            {isPartial ? 'Confirmare Modificare Opțiuni' : 'Confirmare Dezabonare'}
          </h1>

          <p className="mt-2.5 text-center text-sm leading-6 text-slate-600">
            Am transmis un cod de confirmare din 6 cifre pe adresa de email:
          </p>
          <p className="mt-1 text-center font-bold text-blue-800 break-all text-base">
            {verificationSession.email}
          </p>

          {isPartial && (
            <div className="mt-4 rounded-xl bg-blue-50/70 border border-blue-200/80 p-3 text-xs text-slate-700 text-left space-y-1">
              <p><strong>Canale selectate:</strong> {formatSelectedChannelsList(verificationSession.requestedChannels)}</p>
              <p><strong>Arii selectate:</strong> {formatSelectedAreasList(verificationSession.requestedAreas)}</p>
            </div>
          )}

          <form onSubmit={handleVerifyCode} className="mt-7">
            <label
              htmlFor="otp-code-input"
              className="block text-center text-xs font-bold uppercase tracking-wider text-slate-600 mb-3"
            >
              Introduceți codul din 6 cifre
            </label>

            <div className="flex justify-center">
              <input
                id="otp-code-input"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={enteredCode}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setEnteredCode(val);
                  if (verificationError) setVerificationError(null);
                }}
                placeholder="••••••"
                className="h-16 w-full max-w-[280px] rounded-xl border-2 border-slate-300 text-center font-mono text-3xl font-extrabold tracking-[0.4em] text-slate-900 shadow-xs transition-all focus:border-blue-600 focus:outline-none focus:ring-4 focus:ring-blue-100 placeholder:tracking-[0.3em] placeholder:text-slate-300"
                autoFocus
              />
            </div>

            {verificationError && (
              <div
                role="alert"
                className="mt-4 flex items-center justify-center gap-2 rounded-xl bg-red-50 p-3 text-xs font-semibold text-red-700"
              >
                <CircleAlert size={16} className="shrink-0" />
                <span>{verificationError}</span>
              </div>
            )}

            {resendNotice && (
              <div
                role="status"
                className="mt-4 flex items-center justify-center gap-2 rounded-xl bg-emerald-50 p-3 text-xs font-semibold text-emerald-800"
              >
                <MailCheck size={16} className="shrink-0" />
                <span>{resendNotice}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={isVerifyingCode || enteredCode.trim().length !== 6}
              className="mt-6 flex w-full min-h-12 items-center justify-center gap-2 rounded-xl bg-blue-700 px-6 py-3 text-base font-bold text-white shadow-sm transition-all hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-4 focus:ring-blue-200"
            >
              {isVerifyingCode ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Se verifică codul...</span>
                </>
              ) : (
                <>
                  <Check size={18} />
                  <span>Confirmă</span>
                </>
              )}
            </button>
          </form>

          <div className="mt-7 border-t border-slate-100 pt-5 text-center">
            <p className="text-xs text-slate-500 leading-5">
              Nu ați primit codul? Vă rugăm să verificați și dosarul de <strong>Spam / Junk</strong> sau solicitați retrimiterea acestuia.
            </p>

            <div className="mt-3 flex items-center justify-center">
              {resendCooldown > 0 ? (
                <span className="text-xs font-medium text-slate-400">
                  Puteți solicita un cod nou în {resendCooldown}s
                </span>
              ) : (
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={isResending}
                  className="text-xs font-bold text-blue-700 hover:text-blue-900 underline disabled:opacity-50"
                >
                  {isResending ? 'Se retrimite...' : 'Retrimite codul'}
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={handleResetToEmail}
              className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-blue-700"
            >
              <ChevronLeft size={14} /> Modifică opțiunile sau adresa
            </button>
          </div>
        </section>
      </main>
    );
  }

  // =========================================================================
  // ECRANUL 1: Formular Inițial: Doar Email la început, animație la căutare [Referința 2]
  // =========================================================================
  return (
    <main
      id="unsubscribe-main-container"
      className="fixed inset-0 z-50 overflow-y-auto bg-slate-50 px-4 py-10 text-slate-900 sm:px-6 sm:py-14"
    >
      <div className="mx-auto max-w-[960px]">
        {/* Header cu Logo DITA */}
        <header className="mb-8 text-center sm:text-left">
          <img src={ditaLogo} alt="DITA ESTFARM" className="h-auto w-36 mx-auto sm:mx-0" />
          <p className="mt-6 text-sm font-semibold text-blue-800 uppercase tracking-wide">
            S.R.L. DITA ESTFARM
          </p>
          <h1 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight text-slate-950 sm:leading-tight">
            Dezabonare comunicări de marketing direct
          </h1>
          <p className="mt-3 text-sm leading-6 text-slate-600 max-w-3xl">
            Dacă nu mai doriți să primiți buletine informative, oferte comerciale și invitații la evenimente din partea S.R.L. DITA ESTFARM, introduceți mai jos adresa dumneavoastră de email pentru a primi codul de securitate.
          </p>
        </header>

        {/* Card Formular Principal */}
        <form onSubmit={handleRequestCode} noValidate className="rounded-2xl bg-white p-6 sm:p-10 shadow-sm border border-slate-200 space-y-7">
          {/* Câmpul Adresă de Email (unicul câmp afișat inițial) */}
          <div>
            <label htmlFor="unsubscribe-email" className="mb-2 block text-sm font-semibold text-slate-800">
              Adresă de email <span className="ml-1 text-blue-700" aria-hidden="true">*</span>
            </label>

            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-slate-400">
                <Mail size={18} />
              </div>
              <input
                id="unsubscribe-email"
                type="email"
                required
                autoFocus
                autoComplete="email"
                value={email}
                onChange={(e) => {
                  const val = e.target.value;
                  setEmail(val);
                  if (emailError) setEmailError(null);
                }}
                placeholder="ex: nume@domeniu.md"
                aria-invalid={Boolean(emailError)}
                aria-describedby={emailError ? 'email-error' : undefined}
                className={`min-h-12 w-full rounded-xl border bg-white pl-10 pr-10 py-3 text-base text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-700/25 ${
                  emailError ? 'border-red-600 focus:border-red-600' : 'border-slate-300 focus:border-blue-700'
                }`}
              />
              {isLookingUp && (
                <div className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-blue-600">
                  <Loader2 size={20} className="animate-spin" />
                </div>
              )}
            </div>

            {emailError && (
              <p id="email-error" role="alert" className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-red-600">
                <CircleAlert size={14} className="shrink-0" />
                <span>{emailError}</span>
              </p>
            )}
          </div>

          {/* Animație de căutare / extragere date din CRM */}
          {isLookingUp && (
            <div className="rounded-2xl border border-blue-200 bg-blue-50/60 p-6 text-center animate-pulse">
              <Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-600 mb-2" />
              <p className="text-sm font-bold text-blue-950">
                Se caută și se extrag datele din baza CRM...
              </p>
              <p className="text-xs text-blue-700 mt-1">
                Preluăm ariile terapeutice și canalele selectate la acordul dumneavoastră de marketing direct.
              </p>
            </div>
          )}

          {/* Datele extrase: se afișează doar după ce s-a finalizat căutarea cu succes */}
          {hasLookedUp && !isLookingUp && (
            <div className="space-y-8 animate-in fade-in slide-in-from-top-4 duration-500">
              {recipientInfo && (
                <div className="flex items-start gap-3 rounded-xl bg-blue-50/80 border border-blue-200 p-4 text-xs text-blue-950">
                  <UserCheck size={20} className="text-blue-700 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-sm">
                      Identificat: {recipientInfo.name} {recipientInfo.specialization ? `(${recipientInfo.specialization})` : ''}
                    </p>
                    <p className="text-slate-600 mt-0.5 leading-relaxed">
                      Am extras din baza CRM opțiunile dumneavoastră curente. Debifați parțial ariile sau canalele nedorite mai jos, sau deselectați-le pe toate pentru dezabonare completă.
                    </p>
                  </div>
                </div>
              )}

              {!existingFound && (
                <div className="flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-200 p-4 text-xs text-amber-950">
                  <Info size={18} className="text-amber-700 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold">
                      Nu a fost găsit un acord activ de marketing direct pentru adresa {email}.
                    </p>
                    <p className="text-amber-800 mt-0.5">
                      Puteți continua pentru a înregistra dezabonarea completă și retragerea oricăror comunicări promoționale.
                    </p>
                  </div>
                </div>
              )}

              {/* Secțiunea Arii Terapeutice (Grid 3 coloane conform Referinței 2) */}
              <div className="border-t border-slate-200/80 pt-6">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div>
                    <h2 className="text-base font-bold text-slate-950">
                      Selectați ariile terapeutice pentru care doriți să primiți comunicări
                    </h2>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Debifați ariile din care doriți să vă dezabonați. Lăsați bifate doar domeniile de interes.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={toggleSelectAllAreas}
                    className="text-xs font-bold text-blue-700 hover:text-blue-900 underline"
                  >
                    {isAllAreasSelected ? 'Deselectează tot' : 'Selectează tot'}
                  </button>
                </div>

                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {THERAPEUTIC_AREAS.map((area) => {
                    const isChecked = selectedAreas.includes(area.value);
                    return (
                      <label
                        key={area.value}
                        className={`flex min-h-11 cursor-pointer items-center gap-3 rounded-lg px-3.5 py-2.5 transition-colors border ${
                          isChecked
                            ? 'bg-blue-50 text-blue-950 border-blue-200 font-medium'
                            : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                        }`}
                      >
                        <input
                          type="checkbox"
                          value={area.value}
                          checked={isChecked}
                          onChange={() => toggleArea(area.value)}
                          className="h-5 w-5 rounded border-slate-400 text-blue-700 focus:ring-blue-700"
                        />
                        <span className="text-sm font-medium leading-5">{area.label}</span>
                      </label>
                    );
                  })}
                </div>

                {selectedAreas.includes('other') && (
                  <div className="mt-4">
                    <label htmlFor="otherAreaText" className="block text-xs font-semibold text-slate-700 mb-1">
                      Specificați alte arii terapeutice
                    </label>
                    <input
                      id="otherAreaText"
                      type="text"
                      value={otherAreaText}
                      onChange={(e) => setOtherAreaText(e.target.value)}
                      placeholder="ex: Radiologie, Medicină de urgență..."
                      className="min-h-11 w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm text-slate-900 focus:border-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-700/25"
                    />
                  </div>
                )}
              </div>

              {/* Secțiunea Canale de Comunicare Preferate (3 carduri conform Referinței 2) */}
              <div className="border-t border-slate-200/80 pt-6">
                <h2 className="text-base font-bold text-slate-950">
                  Canale de comunicare preferate <span className="text-blue-700">*</span>
                </h2>
                <p className="mt-1 text-xs text-slate-600">
                  Selectați prin ce mijloace acceptați să primiți comunicările și informările din partea reprezentanților noștri (alegeți cel puțin un canal):
                </p>

                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                  {COMMUNICATION_CHANNELS.map((ch) => {
                    const isChecked = selectedChannels.includes(ch.value);
                    return (
                      <label
                        key={ch.value}
                        className={`flex cursor-pointer flex-col justify-between rounded-xl border p-4 transition-all ${
                          isChecked
                            ? 'border-blue-700 bg-blue-50/80 shadow-xs ring-1 ring-blue-700/20'
                            : 'border-slate-200 bg-slate-50/60 hover:bg-slate-100/80 text-slate-800'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            value={ch.value}
                            checked={isChecked}
                            onChange={() => toggleChannel(ch.value)}
                            className="h-5 w-5 rounded border-slate-400 text-blue-700 focus:ring-blue-700"
                          />
                          <span className="text-sm font-bold text-slate-900">{ch.label}</span>
                        </div>
                        <p className="mt-2 text-xs leading-5 text-slate-600">
                          {ch.description}
                        </p>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Alertă de stare dinamică */}
              <div className={`rounded-xl p-4 text-xs leading-relaxed border ${
                isPartialUpdate
                  ? 'bg-blue-50 border-blue-200 text-blue-950'
                  : 'bg-amber-50 border-amber-200 text-amber-950'
              }`}>
                <div className="flex items-start gap-2.5">
                  <Info size={17} className="mt-0.5 shrink-0" />
                  <div>
                    {isPartialUpdate ? (
                      <p>
                        <strong>Actualizare opțiuni:</strong> Ați selectat <strong>{selectedAreas.length}</strong> {selectedAreas.length === 1 ? 'arie terapeutică' : 'arii terapeutice'} și <strong>{selectedChannels.length}</strong> {selectedChannels.length === 1 ? 'canal' : 'canale'}. La confirmarea codului, opțiunile modificate vor fi salvate în CRM.
                      </p>
                    ) : (
                      <p>
                        <strong>Dezabonare:</strong> Ați deselectat toate ariile și canalele de comunicare. La confirmarea codului, veți fi <strong>dezabonat</strong> de la toate comunicările de marketing direct.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Buton Trimitere Cod (Disabled până când se extrag datele din CRM) */}
          <div>
            <button
              type="submit"
              disabled={isButtonDisabled}
              className={`flex w-full min-h-12 items-center justify-center gap-2 rounded-xl px-6 py-3.5 text-base font-bold text-white shadow-sm transition-all focus:outline-none focus:ring-4 ${
                isButtonDisabled
                  ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                  : isPartialUpdate
                  ? 'bg-blue-700 hover:bg-blue-800 focus:ring-blue-200'
                  : 'bg-red-700 hover:bg-red-800 focus:ring-red-200'
              }`}
            >
              {isSubmittingEmail ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Se trimite codul pe email...</span>
                </>
              ) : isLookingUp ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Se extrag datele din baza CRM...</span>
                </>
              ) : !isEmailValid ? (
                <span>Introduceți adresa de email pentru a continua</span>
              ) : !hasLookedUp ? (
                <span>Introduceți adresa de email</span>
              ) : isPartialUpdate ? (
                <>
                  <Mail size={18} />
                  <span>Trimite cod de confirmare</span>
                </>
              ) : (
                <>
                  <Mail size={18} />
                  <span>Trimite cod de dezabonare</span>
                </>
              )}
            </button>
          </div>

          {/* Notă Legală Legea 195/2024 */}
          <div className="flex gap-3 rounded-xl bg-slate-50 p-4 text-xs leading-5 text-slate-600 border border-slate-200/80">
            <Info className="mt-0.5 shrink-0 text-blue-700" size={17} aria-hidden="true" />
            <p>
              Conform <strong>Legii RM nr. 195/2024</strong> privind protecția datelor cu caracter personal, retragerea sau modificarea consimțământului este gratuită și nu afectează colaborarea profesională existentă cu DITA ESTFARM și prelucrarea în CRM în temeiul interesului legitim.
            </p>
          </div>
        </form>

        {/* Link către pagina de consimțământ */}
        <div className="mt-6 text-center">
          <p className="text-xs text-slate-500">
            Ați ajuns aici din greșeală?{' '}
            <a href="/marketing-consent" className="font-bold text-blue-700 hover:text-blue-900 underline">
              Completați sau actualizați formularul de acord
            </a>
          </p>
        </div>
      </div>
    </main>
  );
}
